// src/pages/league-dashboard/standings/RotisserieTable.js
//
// Extracted from inline closure in LeagueStandings.js per IMPORTANT
// batch 6 commit 6a. Closure-→-prop conversion: parent's `league`,
// `leagueId` promoted to declared props (added to the existing 7
// declared props). blendHighlight helper co-located here as the
// only consumer.
import React, { useState } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import TeamLinkDropdown from '../../../components/league-dashboard/TeamLinkDropdown';
import TeamLogo from './TeamLogo';

// Blend a hex foreground color at 25% opacity over a hex background color.
// Returns an opaque hex string so table cells never use semi-transparent backgrounds.
function blendHighlight(fgHex, bgHex) {
  const parse = (h) => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
  const fg = parse(fgHex || '#eab308');
  const bg = parse(bgHex || '#1a1a1a');
  const blend = (f, b) => Math.round(f * 0.25 + b * 0.75);
  const r = blend(fg[0], bg[0]), g = blend(fg[1], bg[1]), b = blend(fg[2], bg[2]);
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
}

// DD-STAT-CONSOLIDATION Item 2: single standings table for BOTH pages.
// `shellless` renders just the table (no card/header) so league-home can drop it into
// its own card — supersedes the former league-home/RotisserieStandings duplicate.
// Data shape is invariant across all /standings period variants (agent-verified):
// `stats.{hitting,pitching}[cat]` is a scalar and `category_points[cat]` is always
// present — so this reads them directly (no object-value branch, no category_ranks
// fallback; those were dead in both copies).
const RotisserieTable = ({ data, title, throughDate: tableThroughDate, icon: Icon, userTeam, isMobile, dateStepper, league, leagueId, shellless = false }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'totalPoints', direction: 'desc' });

  if (!data) return null;

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Sort teams based on current sort configuration
  const sortedTeams = [...data.teams].sort((a, b) => {
    let aValue, bValue;

    if (sortConfig.key === 'totalPoints') {
      aValue = a.totalPoints || a.total_points || 0;
      bValue = b.totalPoints || b.total_points || 0;
    } else if (sortConfig.key === 'team_name') {
      aValue = a.team_name || '';
      bValue = b.team_name || '';
    } else if (sortConfig.key.startsWith('points_')) {
      const cat = sortConfig.key.replace('points_', '');
      aValue = a.category_points?.[cat] || 0;
      bValue = b.category_points?.[cat] || 0;
    } else if (sortConfig.key.startsWith('stats_')) {
      const cat = sortConfig.key.replace('stats_', '');
      aValue = a.stats?.hitting?.[cat] ?? a.stats?.pitching?.[cat] ?? 0;
      bValue = b.stats?.hitting?.[cat] ?? b.stats?.pitching?.[cat] ?? 0;
    } else {
      aValue = 0;
      bValue = 0;
    }

    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });

  const hittingCategories = data.categories.hitting;
  const pitchingCategories = data.categories.pitching;
  const numTeams = data.teams.length;

  // Sort indicator component
  const SortIndicator = ({ isActive, direction }) => {
    if (!isActive) return null;
    return (
      <span className="ml-1">
        {direction === 'desc' ? '▼' : '▲'}
      </span>
    );
  };

  const tableBlock = (
      <div className={shellless ? 'overflow-x-auto' : 'p-4 overflow-x-auto'}>
        <table className="w-full text-xs">
          <thead>
            <tr className={`${dynastyTheme.classes.bg.darkLighter} border-b ${dynastyTheme.classes.border.neutral}`}>
              <th
                className={`py-1 px-1 text-center ${dynastyTheme.classes.text.white} font-bold sticky left-0 z-10`}
                style={{
                  backgroundColor: '#292524',
                  minWidth: '30px',
                  maxWidth: '30px',
                  width: '30px'
                }}
              >
                RK
              </th>
              <th
                className={`${isMobile ? 'py-0.5 px-1' : 'py-1 px-2'} text-left ${dynastyTheme.classes.text.white} font-bold sticky left-0 z-10 ${isMobile ? 'min-w-[175px]' : 'min-w-[250px]'} cursor-pointer hover:bg-neutral-800/50`}
                style={{
                  backgroundColor: '#292524',
                  left: '30px'
                }}
                onClick={() => handleSort('team_name')}
              >
                Team
                <SortIndicator isActive={sortConfig.key === 'team_name'} direction={sortConfig.direction} />
              </th>

              {/* Hitting Categories */}
              {hittingCategories.map(cat => (
                <th
                  key={cat}
                  className={`py-1 px-1 text-center ${dynastyTheme.classes.text.primary} font-semibold min-w-[40px] text-[10px] cursor-pointer hover:bg-neutral-800/50`}
                  onClick={() => handleSort(`points_${cat}`)}
                >
                  {cat}
                  <SortIndicator isActive={sortConfig.key === `points_${cat}`} direction={sortConfig.direction} />
                </th>
              ))}

              {/* Separator between hitting and pitching */}
              <th className="w-[2px] p-0 bg-neutral-600"></th>

              {/* Pitching Categories */}
              {pitchingCategories.map(cat => (
                <th
                  key={cat}
                  className={`py-1 px-1 text-center ${dynastyTheme.classes.text.primary} font-semibold min-w-[40px] text-[10px] cursor-pointer hover:bg-neutral-800/50`}
                  onClick={() => handleSort(`points_${cat}`)}
                >
                  {cat}
                  <SortIndicator isActive={sortConfig.key === `points_${cat}`} direction={sortConfig.direction} />
                </th>
              ))}

              <th
                className={`py-1 px-1 text-center ${dynastyTheme.classes.text.white} font-bold min-w-[45px] cursor-pointer hover:bg-neutral-800/50`}
                onClick={() => handleSort('totalPoints')}
              >
                TOTAL
                <SortIndicator isActive={sortConfig.key === 'totalPoints'} direction={sortConfig.direction} />
              </th>
              <th className={`py-1 px-1 text-center ${dynastyTheme.classes.text.success} font-bold min-w-[40px]`}>
                +/-
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team, teamIndex) => {
              const isUserTeam = userTeam && (team.team_id === userTeam.team_id || team.user_id === userTeam.user_id);
              const totalPoints = team.totalPoints || team.total_points || 0;
              const leaderPoints = sortedTeams[0].totalPoints || sortedTeams[0].total_points || 0;

              // Alternating row colors - every other row (not every 2 rows)
              const pointsRowIndex = teamIndex * 2; // 0, 2, 4, 6...
              const statsRowIndex = teamIndex * 2 + 1; // 1, 3, 5, 7...
              const pointsRowBgColor = pointsRowIndex % 2 === 0 ? '#1a1a1a' : '#292524';
              const statsRowBgColor = statsRowIndex % 2 === 0 ? '#1a1a1a' : '#292524';

              // User team highlight: compute a single OPAQUE blended color so every
              // cell (sticky or not) uses the same value with zero transparency.
              // This prevents double-layering from semi-transparent tr + td backgrounds.
              const finalPointsRowBg = isUserTeam
                ? blendHighlight(userTeam?.team_colors?.primary, pointsRowBgColor)
                : pointsRowBgColor;
              const finalStatsRowBg = statsRowBgColor;

              // All cells (including sticky) use the same opaque color.
              const stickyPointsRowBg = isUserTeam ? finalPointsRowBg : pointsRowBgColor;
              const stickyStatsRowBg = statsRowBgColor;

              return (
                <React.Fragment key={team.team_id}>
                  {/* Points Row - No border (grid line only on stats row) */}
                  <tr style={{ backgroundColor: finalPointsRowBg }}>
                    {/* RK Column */}
                    <td
                      className={`py-0.5 px-1 text-center sticky left-0 z-10`}
                      style={{
                        backgroundColor: stickyPointsRowBg,
                        minWidth: '30px',
                        maxWidth: '30px',
                        width: '30px',
                        fontSize: '11px',
                        color: dynastyTheme.tokens.colors.neutral[300]
                      }}
                    >
                      {teamIndex + 1}
                    </td>
                    <td className={`${isMobile ? 'py-0.5 px-1' : 'py-0.5 px-2'} sticky left-0 z-10 ${isMobile ? 'min-w-[175px]' : 'min-w-[250px]'}`}
                      style={{
                        backgroundColor: stickyPointsRowBg,
                        left: '30px'
                      }}>
                      <div className="flex items-center gap-2">
                        <TeamLogo team={team} size="small" />
                        <div className="flex-1 min-w-0">
                          <TeamLinkDropdown
                            team={team}
                            leagueId={leagueId}
                            className={`${isUserTeam ? dynastyTheme.classes.text.primary + ' font-bold' : dynastyTheme.classes.text.white + ' font-medium'} ${isMobile ? 'text-[12px]' : 'text-sm'} truncate block`}
                          />
                          {team.manager_name && (
                            <div className={`${dynastyTheme.classes.text.neutralLight} ${isMobile ? 'text-[9px]' : 'text-[10px]'} truncate`}>
                              {team.manager_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Hitting points */}
                    {hittingCategories.map(cat => {
                      const points = team.category_points?.[cat] || 0;
                      const isMax = points === numTeams;
                      const isMin = points === 1;
                      return (
                        <td key={cat} className={`py-0.5 ${isMobile ? 'px-0.5' : 'px-1'} text-center ${isMobile ? 'text-[11px]' : 'text-[13px]'} font-bold ${
                          isMax ? dynastyTheme.classes.text.success :
                          isMin ? dynastyTheme.classes.text.error :
                          dynastyTheme.classes.text.white
                        }`}>
                          {points}
                        </td>
                      );
                    })}

                    {/* Separator */}
                    <td className="w-[2px] p-0 bg-neutral-600"></td>

                    {/* Pitching points */}
                    {pitchingCategories.map(cat => {
                      const points = team.category_points?.[cat] || 0;
                      const isMax = points === numTeams;
                      const isMin = points === 1;
                      return (
                        <td key={cat} className={`py-0.5 ${isMobile ? 'px-0.5' : 'px-1'} text-center ${isMobile ? 'text-[11px]' : 'text-[13px]'} font-bold ${
                          isMax ? dynastyTheme.classes.text.success :
                          isMin ? dynastyTheme.classes.text.error :
                          dynastyTheme.classes.text.white
                        }`}>
                          {points}
                        </td>
                      );
                    })}

                    <td className={`py-0.5 px-1 text-center font-bold text-sm ${dynastyTheme.classes.text.primary}`}>
                      {totalPoints}
                    </td>
                    <td className={`py-0.5 px-1 text-center font-semibold text-[12px] ${
                      teamIndex === 0 && sortConfig.key === 'totalPoints' && sortConfig.direction === 'desc' ? dynastyTheme.classes.text.success :
                      teamIndex < Math.floor(numTeams / 3) ? dynastyTheme.classes.text.warning :
                      dynastyTheme.classes.text.error
                    }`}>
                      {teamIndex === 0 && sortConfig.key === 'totalPoints' && sortConfig.direction === 'desc' ? '-' : `-${Math.abs(leaderPoints - totalPoints).toFixed(1)}`}
                    </td>
                  </tr>

                  {/* Stats Row - Border only on stats row (every 2nd row) */}
                  <tr
                    className={`border-b ${dynastyTheme.classes.border.neutral}`}
                    style={{ backgroundColor: finalStatsRowBg }}
                  >
                    {/* RK Column - Empty for stats row */}
                    <td
                      className={`py-0.5 px-1 text-center sticky left-0 z-10`}
                      style={{
                        backgroundColor: stickyStatsRowBg,
                        minWidth: '30px',
                        maxWidth: '30px',
                        width: '30px'
                      }}
                    >
                    </td>
                    <td className={`${isMobile ? 'py-0.5 px-1' : 'py-1 px-2'} sticky left-0 z-10 ${dynastyTheme.classes.text.neutralLight} ${isMobile ? 'min-w-[175px]' : 'min-w-[250px]'} text-[10px] font-medium`}
                      style={{
                        backgroundColor: stickyStatsRowBg,
                        left: '30px'
                      }}>
                      <span className={isMobile ? 'pl-3' : 'pl-3'} style={{ color: dynastyTheme.tokens.colors.neutral[300] }}>Total Stats</span>
                    </td>

                    {/* Hitting stats */}
                    {hittingCategories.map(cat => {
                      const statData = team.stats?.hitting?.[cat];
                      const value = statData || 0; // scalar (agent-verified /standings shape; object branch was dead)

                      let displayValue = value || 0;
                      if (typeof value === 'number') {
                        if (cat === 'AVG' || cat === 'OBP' || cat === 'SLG' || cat === 'OPS' || cat === 'WHIP') {
                          displayValue = formatBattingAvg(value);
                        } else if (cat === 'ERA') {
                          displayValue = formatERA(value);
                        } else if (Number.isInteger(value)) {
                          displayValue = value;
                        } else {
                          displayValue = value.toFixed(1);
                        }
                      }

                      return (
                        <td key={`${cat}-stats`} className={`${isMobile ? 'py-0.5 px-0.5' : 'py-1 px-1'} text-center ${isMobile ? 'text-[10px]' : 'text-[11px]'} ${dynastyTheme.classes.text.neutralLight}`}>
                          {displayValue}
                        </td>
                      );
                    })}

                    {/* Separator */}
                    <td className="w-[2px] p-0 bg-neutral-600"></td>

                    {/* Pitching stats */}
                    {pitchingCategories.map(cat => {
                      const statData = team.stats?.pitching?.[cat];
                      const value = statData || 0; // scalar (agent-verified /standings shape; object branch was dead)

                      let displayValue = value || 0;
                      if (typeof value === 'number') {
                        if (cat === 'AVG' || cat === 'OBP' || cat === 'SLG' || cat === 'OPS' || cat === 'WHIP') {
                          displayValue = formatBattingAvg(value);
                        } else if (cat === 'ERA') {
                          displayValue = formatERA(value);
                        } else if (Number.isInteger(value)) {
                          displayValue = value;
                        } else {
                          displayValue = value.toFixed(1);
                        }
                      }

                      return (
                        <td key={`${cat}-stats`} className={`${isMobile ? 'py-0.5 px-0.5' : 'py-1 px-1'} text-center ${isMobile ? 'text-[10px]' : 'text-[11px]'} ${dynastyTheme.classes.text.neutralLight}`}>
                          {displayValue}
                        </td>
                      );
                    })}

                    <td colSpan="2"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
  );

  if (shellless) return tableBlock;

  return (
    <div className={dynastyTheme.components.card.base}>
      <div className={`flex items-center justify-between px-4 py-2 border-b ${dynastyTheme.classes.border.neutral}`}>
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />}
          <h3 className={`text-base font-bold ${dynastyTheme.classes.text.white} flex items-center gap-2`}>
            {title}
            {dateStepper || (tableThroughDate && (
              <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginLeft: 8 }}>
                (through {new Date(tableThroughDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
              </span>
            ))}
          </h3>
        </div>
        {title?.includes('Current') && (
          <span className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            {league?.league_status?.replace(/_/g, ' ').toUpperCase()}
          </span>
        )}
      </div>
      {tableBlock}
    </div>
  );
};

export default RotisserieTable;
